import React, {FC, useCallback, useMemo} from 'react';
import {useRouter} from 'next/router';
import style from './style.module.scss';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {getTabIdFromUrl} from 'modules/stock-x/helpers/getTabIdFromUrl';
import {IStock} from 'modules/stock-x/types';
import {omit} from 'lodash';
import {Box} from 'components/Grid';
import {useFiltersGtag} from 'hooks/analytics/gtag/catalog/useFiltersGtag';
import {EventAction, EventContextType, EventLabel} from 'modules/google-analytics/constants';

interface IProps {
    tabs: IStock['tabs'];
}

export const TabList: FC<IProps> = ({tabs = []}) => {
    const router = useRouter();

    const sendFiltersGtag = useFiltersGtag();

    const activeTabCode = useMemo(() => {
        const tabCode = getTabIdFromUrl(router.asPath, router.query);

        if (!tabCode) {
            return null;
        }

        return String(tabCode);
    }, [router.asPath, router.query]);

    const activeTabTitle = useMemo(() => {
        return tabs.find((tab) => tab.code === activeTabCode)?.title;
    }, [activeTabCode, tabs]);

    const handleTabChange = useCallback(
        async (tabId: string, title: string) => {
            sendFiltersGtag(
                tabId ? title : activeTabTitle,
                tabId ? EventAction.AddFilter : EventAction.RemoveFilter,
                EventContextType.TabFilter,
                EventLabel.TabFilter,
            );

            await router.push(
                {
                    pathname: router.pathname,
                    query: !tabId ? omit(router.query, 'tabId') : {...router.query, tabId},
                },
                undefined,
                {
                    scroll: false,
                    shallow: true,
                },
            );
        },
        [activeTabTitle, router, sendFiltersGtag],
    );

    if (0 === tabs.length) {
        return null;
    }

    return (
        <Box className={style.root}>
            {tabs.map(({code, title}, index) => {
                const isActive = activeTabCode === code || 1 === tabs.length || (!activeTabCode && 0 === index);

                return (
                    <label
                        key={`${title}-${code}`}
                        data-skeleton-item={true}
                        className={classNames(style.tab, {
                            [style.isChecked]: isActive,
                        })}
                    >
                        <input
                            checked={isActive}
                            className={style.checkbox}
                            onChange={() => handleTabChange(code, title)}
                            type={'radio'}
                        />
                        <Typography className={style.content} variant={'label-14-r'}>
                            {title}
                        </Typography>
                    </label>
                );
            })}
        </Box>
    );
};
